import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, Download, FileAudio, FolderOpen, Loader2 } from 'lucide-react';
import type { AudioGenerationResponse } from '../types';
import type { AudioJobResult } from '../services/polling/audioPollAdapter';
import { FormatterService } from '../services/formatter.service';
import { zipDownloadService, multilingualAudioStrategy } from '../services/zip';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from './Toast';
import { downloadFile } from '@/utils/downloadProxy';
import { isDesktopApp } from '@/utils/apiMode';
import { openVideoFolder as electronOpenFolder } from '@/utils/electronBridge';
import { useTranslation } from '@/hooks/useTranslation';
import { ResultMedia } from './common/ResultMedia';

interface AudioPlayerProps {
  url: string;
  metadata?: AudioGenerationResponse;
  /**
   * Re-read the job and resolve its result with freshly signed links; rejects
   * when the job can no longer be read. `url` and the multilingual links in
   * `metadata` expire an hour after the worker signed them.
   */
  refreshResult: () => Promise<AudioJobResult>;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ url, metadata, refreshResult }) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLMediaElement>(null);
  const { toasts, showToast, removeToast } = useToast();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);

  // Check if running in Electron desktop app (uses centralized apiMode utility)
  const isDesktop = isDesktopApp();

  // Count total languages (1 = single audio, 2+ = multiple languages need ZIP)
  const multilingualCount = metadata?.multilingual_audio_urls
    ? Object.keys(metadata.multilingual_audio_urls).length
    : 0;
  const totalLanguages = 1 + multilingualCount; // Primary + additional languages
  const needsZipDownload = totalLanguages >= 2;

  // Desktop app: the file is already saved locally, so show "Show in Folder"
  // instead of triggering a browser download (which in Electron surfaces a
  // confusing native `blob:file://...` notification instead of a real file).
  // audio_path points at the job's project root; multilingual tracks (if any)
  // live in that same project's "narration" subfolder, so opening the folder
  // for the primary track also gives access to every language variant.
  const showOpenFolder = isDesktop && !!metadata?.audio_path;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const readDuration = () => (Number.isFinite(audio.duration) ? audio.duration : 0);

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    // `durationchange` also covers streamed sources whose duration is only
    // known (or corrected) after `loadedmetadata`.
    const handleDurationChange = () => {
      setDuration(readDuration());
    };

    const handleEnded = () => {
      setIsPlaying(false);
      // The last `timeupdate` fires a fraction of a second before playback
      // actually ends, so without this the slider freezes just short of the
      // end. Snap it to the real duration.
      setCurrentTime(readDuration());
    };

    // The element itself is the source of truth for play state: autoplay
    // rejections, the OS media keys and end-of-media all change it without
    // going through our button.
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleDurationChange);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleDurationChange);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      // `isPlaying` is updated by the element's play/pause events, so a
      // rejected play() leaves the button in the correct (paused) state.
      audio.play().catch((error: unknown) => {
        console.error('Audio playback failed:', error);
      });
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newVolume = parseFloat(e.target.value);
    audio.volume = newVolume;
    setVolume(newVolume);
  };

  // Guard against duration === 0 (metadata not loaded yet), which would make
  // the gradient stop resolve to `NaN%` and render an invalid background.
  const progressPercent = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renewAudioUrl = useCallback(
    async (): Promise<string> => (await refreshResult()).audioUrl,
    [refreshResult]
  );

  // The links this player holds die an hour after the worker signed them, while
  // it stays on screen for a day. A download re-reads the job first (the backend
  // re-signs on every read). Returns null when no fresh result could be had --
  // the user is told, rather than handed the dead link already held.
  const getFreshResult = useCallback(async (): Promise<AudioJobResult | null> => {
    try {
      return await refreshResult();
    } catch (error) {
      console.error('Could not renew the audio links:', error);
      showToast('error', t('result.linkRenewal.title'), t('result.linkRenewal.message'));
      return null;
    }
  }, [refreshResult, showToast, t]);

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const fresh = await getFreshResult();
      if (!fresh) return;
      await downloadFile(fresh.audioUrl, metadata?.audio_path || 'audio.mp3');
    } catch {
      showToast('error', t('audioPlayer.downloadFailed'), t('audioPlayer.downloadFileError'));
    } finally {
      setIsDownloading(false);
    }
  }, [getFreshResult, metadata?.audio_path, isDownloading, showToast, t]);

  // Reveal the already-local audio file in Finder/Explorer (desktop app only).
  const handleOpenFolder = useCallback(async () => {
    const filePath = metadata?.audio_path;
    if (!filePath) {
      showToast('error', t('results.openFolder.errorTitle'), t('results.openFolder.noLocalPath'));
      return;
    }
    const success = await electronOpenFolder(filePath);
    if (!success) {
      showToast('error', t('results.openFolder.errorTitle'), t('results.openFolder.errorMessage'));
    }
  }, [metadata?.audio_path, showToast, t]);

  const handleDownloadZip = useCallback(async () => {
    if (!metadata || !needsZipDownload) return;

    setIsDownloadingZip(true);
    setZipProgress(0);

    try {
      const fresh = await getFreshResult();
      if (!fresh) return;
      const files = multilingualAudioStrategy.buildFileEntries(fresh.metadata);
      const archiveName = multilingualAudioStrategy.getArchiveName(fresh.metadata);

      await zipDownloadService.createAndDownload(
        {
          archiveName,
          files,
          includeManifest: false, // Simple ZIP with just audio files
        },
        (progress) => {
          setZipProgress(progress.progress);
        }
      );
    } catch (error) {
      console.error('Failed to download ZIP:', error);
      showToast('error', t('audioPlayer.downloadFailed'), t('audioPlayer.downloadArchiveError'));
    } finally {
      setIsDownloadingZip(false);
      setZipProgress(0);
    }
  }, [metadata, needsZipDownload, getFreshResult, showToast, t]);

  // Unified download handler: single file for 1 language, ZIP for 2+
  const handleUnifiedDownload = useCallback(() => {
    if (needsZipDownload) {
      void handleDownloadZip();
    } else {
      void handleDownload();
    }
  }, [needsZipDownload, handleDownloadZip, handleDownload]);

  return (
    <div className="card p-6 space-y-4" data-testid="audio-player">
      {/* Audio element (no controls, so not drawn): renews its link when it dies */}
      <ResultMedia
        ref={audioRef}
        kind="audio"
        url={url}
        renewUrl={renewAudioUrl}
        preload="metadata"
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-purple-900/30 rounded-lg flex items-center justify-center">
            <FileAudio className="h-6 w-6 text-purple-300" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{t('audioPlayer.generatedAudio')}</h3>
            {metadata?.script && (
              <p className="text-sm text-white/60">
                {t('audioPlayer.words', { count: metadata.word_count ?? 0 })} •{' '}
                {formatTime(metadata.duration || duration)}
                {metadata.file_size && ` • ${FormatterService.formatFileSize(metadata.file_size)}`}
              </p>
            )}
          </div>
        </div>

        {/* Primary action: desktop app shows "Show in Folder" (file is already
            local); web and multilingual ZIP downloads keep the Download button. */}
        {showOpenFolder ? (
          <button
            onClick={handleOpenFolder}
            className="btn-secondary flex items-center space-x-2"
            data-testid="open-folder-button"
          >
            <FolderOpen className="h-4 w-4" />
            <span>{t('result.buttons.showInFinder')}</span>
          </button>
        ) : (
          <button
            onClick={handleUnifiedDownload}
            disabled={isDownloading || isDownloadingZip}
            className="btn-secondary flex items-center space-x-2"
            data-testid="download-button"
          >
            {isDownloadingZip ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>
                  {zipProgress > 0 ? `${Math.round(zipProgress)}%` : t('audioPlayer.preparing')}
                </span>
              </>
            ) : isDownloading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('common.downloading')}</span>
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                <span>{t('common.download')}</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Player Controls */}
      <div className="space-y-3">
        {/* Play/Pause and Seek */}
        <div className="flex items-center space-x-4">
          <button
            onClick={togglePlayPause}
            className="w-10 h-10 rounded-full bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center transition-colors"
            data-testid="play-pause-button"
          >
            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
          </button>

          <div className="flex-1 flex items-center space-x-2">
            <span className="text-sm text-white/60 w-12 text-right">{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, rgb(79, 70, 229) 0%, rgb(79, 70, 229) ${progressPercent}%, rgba(255, 255, 255, 0.12) ${progressPercent}%, rgba(255, 255, 255, 0.12) 100%)`,
              }}
              data-testid="seek-slider"
            />
            <span className="text-sm text-white/60 w-12">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume Control */}
        <div className="flex items-center space-x-3 pl-14">
          <Volume2 className="h-4 w-4 text-white/60" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            className="w-32 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, rgb(79, 70, 229) 0%, rgb(79, 70, 229) ${volume * 100}%, rgba(255, 255, 255, 0.12) ${volume * 100}%, rgba(255, 255, 255, 0.12) 100%)`,
            }}
            data-testid="volume-slider"
          />
          <span className="text-sm text-white/60 w-10">{Math.round(volume * 100)}%</span>
        </div>
      </div>

      {/* Metadata Display */}
      {metadata?.script && (
        <div className="mt-4 p-4 bg-black/20 rounded-lg border border-white/10">
          <h4 className="text-sm font-semibold text-white/80 mb-2">
            {t('audioPlayer.generatedScript')}
          </h4>
          <p className="text-sm text-white/70 whitespace-pre-wrap">{metadata.script}</p>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Additional Metadata */}
      {metadata?.metadata && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          {metadata.metadata.tts_provider ? (
            <div>
              <span className="text-white/50">{t('audioPlayer.provider')}</span>
              <span className="ml-2 font-medium text-white">
                {String(metadata.metadata.tts_provider as string)}
              </span>
            </div>
          ) : null}
          {metadata.metadata.language ? (
            <div>
              <span className="text-white/50">{t('audioPlayer.language')}</span>
              <span className="ml-2 font-medium text-white">
                {String(metadata.metadata.language as string)}
              </span>
            </div>
          ) : null}
          {metadata.metadata.voice_id ? (
            <div>
              <span className="text-white/50">{t('audioPlayer.voice')}</span>
              <span className="ml-2 font-medium text-white">
                {String(metadata.metadata.voice_id as string)}
              </span>
            </div>
          ) : null}
          {metadata.metadata.mode ? (
            <div>
              <span className="text-white/50">{t('audioPlayer.mode')}</span>
              <span className="ml-2 font-medium text-white">
                {String(metadata.metadata.mode as string)}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
